import React, { useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Wallet, AlertTriangle, Clock, CheckCircle, XCircle,
  ChevronRight, RefreshCw, ArrowLeftRight,
  RotateCcw, Download,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { useGameWalletProviders, useGameWalletStatistics } from '../../../hooks/useApi';
import { usePermission } from '../../../hooks/useAuth';
import { cn, exportToExcel } from '../../../utils';
import { apiClient } from '../../../services/api';
import { GameWallet } from '../../../types';
import { Card, CardContent } from '../../ui/Card';
import Button from '../../ui/Button';
import { LoadingSpinner } from '../../ui/Loading';
import ExportConfirmModal from '../../ui/ExportConfirmModal';
import WalletsTab from './tabs/WalletsTab';
import SyncIssuesTab from './tabs/SyncIssuesTab';
import TransactionsTab from './tabs/TransactionsTab';

const EXPORT_LIMIT = 1000;

type TabId = 'wallets' | 'sync-issues' | 'transactions';

const TABS: { id: TabId; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'wallets', label: 'Wallets', icon: Wallet },
  { id: 'sync-issues', label: 'Sync Issues', icon: AlertTriangle },
  { id: 'transactions', label: 'Transactions', icon: ArrowLeftRight },
];

const TAB_IDS: TabId[] = ['wallets', 'sync-issues', 'transactions'];

function parseTabId(raw: string | null): TabId {
  if (raw && TAB_IDS.includes(raw as TabId)) {
    return raw as TabId;
  }
  return 'wallets';
}

const GameWalletProviderDetail: React.FC = () => {
  const { providerId } = useParams<{ providerId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = useMemo(() => parseTabId(searchParams.get('tab')), [searchParams]);

  const setActiveTab = useCallback(
    (tab: TabId) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab === 'wallets') {
            next.delete('tab');
          } else {
            next.set('tab', tab);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  // "Retry failed syncs" is a bulk write (provider ops) — hidden for view-only roles (platform_admin).
  const canSync = usePermission('gameProviderManagement');

  const { data: providersData, isLoading: providersLoading } = useGameWalletProviders();
  const { data: statsData, isLoading: statsLoading, refetch: refetchStats } = useGameWalletStatistics(
    providerId ? { gameProviderId: providerId } : undefined,
  );

  const provider = providersData?.data?.items?.find((p) => p.id === providerId);
  const stats = statsData?.data;

  const handleRetryFailedSyncs = async () => {
    if (!providerId || isRetrying) return;
    setIsRetrying(true);
    try {
      const res = await apiClient.getGameWallets({ gameProviderId: providerId, syncStatus: 'failed', page: 1, limit: 500 });
      const failed: GameWallet[] = res?.data?.items ?? [];
      if (failed.length === 0) {
        toast('No failed wallets to retry', { icon: 'ℹ️' });
        return;
      }
      const results = await Promise.allSettled(
        failed.map((w) => apiClient.syncGameWallet(w.id)),
      );
      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      const errored = results.filter((r) => r.status === 'rejected').length;
      if (errored === 0) {
        toast.success(`Queued ${succeeded} wallet sync${succeeded !== 1 ? 's' : ''}`);
      } else {
        toast(`Queued ${succeeded}, failed to queue ${errored}`, { icon: '⚠️' });
      }
      void refetchStats();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to retry syncs');
    } finally {
      setIsRetrying(false);
    }
  };

  const handleExportConfirm = async () => {
    if (!providerId) return;
    setIsExporting(true);
    try {
      const res = await apiClient.getGameWallets({ gameProviderId: providerId, page: 1, limit: EXPORT_LIMIT });
      const list: GameWallet[] = res?.data?.items ?? [];
      const rows = list.map((w) => ({
        'User ID': w.userId,
        'User (Phone)': w.user?.phone ?? '',
        'User (Email)': w.user?.email ?? '',
        'Provider': w.provider?.name ?? w.provider?.code ?? w.gameProviderId,
        'Temp Wallet Balance': w.walletBalance,
        'In-Game Balance': w.inGameBalance,
        'Sync Status': w.syncStatus,
        'Last Synced At': w.lastSyncedAt ?? '',
        'Created At': w.created_at ?? '',
      }));
      exportToExcel(rows, {
        sheetName: 'Game Wallets',
        fileName: `game-wallets-${provider?.code ?? 'export'}-${format(new Date(), 'yyyy-MM-dd')}`,
      });
      toast.success('Export completed');
      setShowExportModal(false);
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  if (providersLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (!provider && !providersLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <p className="text-muted-foreground text-sm">Provider not found.</p>
        <Button variant="outline" size="sm" onClick={() => navigate('/wallets')}>
          Back to Game Wallets
        </Button>
      </div>
    );
  }

  const syncCounts = stats?.syncStatusCounts;

  return (
    <div className="space-y-5">
      {/* Hero card with gradient */}
      <div className="bg-gradient-to-br from-white to-blue-50 border border-border rounded-2xl p-6 shadow-sm animate-in fade-in slide-in-from-top-2 duration-300">
        <nav className="flex items-center gap-1.5 text-sm mb-4">
          <button
            type="button"
            onClick={() => navigate('/wallets')}
            className="text-blue-600 hover:text-blue-700 transition-colors font-semibold"
          >
            Game Wallets
          </button>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="text-foreground font-semibold">{provider?.name ?? '…'}</span>
        </nav>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center font-black text-blue-600 text-xl shrink-0 transition-shadow duration-300 hover:shadow-md hover:shadow-blue-100">
              {provider?.code?.slice(0, 2).toUpperCase()}
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight leading-tight">
                {provider?.name} Wallets
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Provider wallet detail for monitoring player balances, sync issues, and wallet movements.
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap shrink-0">
            {canSync && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => void handleRetryFailedSyncs()}
                disabled={isRetrying}
                className={cn('transition-all duration-200', isRetrying && 'animate-pulse')}
              >
                <RotateCcw className={cn('h-4 w-4 mr-1.5 transition-transform', isRetrying && 'animate-spin')} />
                {isRetrying ? 'Retrying…' : 'Retry failed syncs'}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowExportModal(true)}>
              <Download className="h-4 w-4 mr-1.5" />
              Export Wallet CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetchStats()}
              disabled={statsLoading}
              className="transition-all duration-200 hover:scale-105 active:scale-95"
            >
              <RefreshCw className={cn('h-4 w-4 transition-transform duration-500', statsLoading ? 'animate-spin' : 'hover:rotate-180')} />
            </Button>
          </div>
        </div>
      </div>

      {/* KPI bar */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300 delay-100">
        <KpiCard
          label="Total Wallets"
          value={statsLoading ? '…' : String(stats?.totalWallets ?? '—')}
          icon={<Wallet className="h-4 w-4" />}
        />
        <KpiCard
          label="In Sync"
          value={statsLoading ? '…' : String(syncCounts?.in_sync ?? '—')}
          icon={<CheckCircle className="h-4 w-4" />}
          className="text-green-700"
        />
        <KpiCard
          label="Pending"
          value={statsLoading ? '…' : String(syncCounts?.pending ?? '—')}
          icon={<Clock className="h-4 w-4" />}
          className="text-amber-700"
        />
        <KpiCard
          label="Failed"
          value={statsLoading ? '…' : String(syncCounts?.failed ?? '—')}
          icon={<XCircle className="h-4 w-4" />}
          className="text-destructive"
        />
        <KpiCard
          label="Never Synced"
          value={statsLoading ? '…' : String(syncCounts?.never_synced ?? '—')}
          icon={<AlertTriangle className="h-4 w-4" />}
          className="text-muted-foreground"
        />
      </div>

      {/* Tabs section */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-wrap gap-2 border-b border-border pb-3 mb-5">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border transition-all duration-200 hover:scale-[1.03] active:scale-95',
                    isActive
                      ? 'bg-foreground text-background border-foreground shadow-sm'
                      : 'bg-white text-foreground border-border hover:bg-muted/50',
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {activeTab === 'wallets' && providerId && (
            <WalletsTab providerId={providerId} providerName={provider?.name} />
          )}
          {activeTab === 'sync-issues' && providerId && (
            <SyncIssuesTab providerId={providerId} />
          )}
          {activeTab === 'transactions' && providerId && (
            <TransactionsTab providerId={providerId} providerName={provider?.name} />
          )}
        </CardContent>
      </Card>

      <ExportConfirmModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onConfirm={handleExportConfirm}
        isExporting={isExporting}
        title="Export to Excel"
        message={`Only the latest ${EXPORT_LIMIT.toLocaleString()} records will be exported. Do you want to continue?`}
      />
    </div>
  );
};

const KpiCard: React.FC<{
  label: string;
  value: string;
  icon: React.ReactNode;
  className?: string;
}> = ({ label, value, icon, className }) => (
  <Card className="transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
    <CardContent className="p-4">
      <div className={cn('flex items-center gap-1.5 text-muted-foreground mb-2', className)}>
        {icon}
        <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
      </div>
      <p className={cn(
        'text-2xl font-bold tracking-tight transition-all duration-300',
        value === '…' && 'animate-pulse text-muted-foreground',
        className,
      )}>{value}</p>
    </CardContent>
  </Card>
);

export default GameWalletProviderDetail;
